import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { ContractsExpandable } from '@dealstack/frontend/shared/components/ContractsExpandable/ContractsExpandable';
import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Option, Select } from '@dealstack/frontend/shared/components/forms/Select/Select';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { DeleteButtonSquare } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { ModalLayer } from '@dealstack/frontend/shared/components/fundament/Modal/ModalLayer';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import { Cell, HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { isReviewSignatureType } from '@dealstack/shared/lib/common/signatures';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';
import { get } from 'lodash';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { ContractCounterpartiesModal } from './ContractCounterpartiesModal';
import { ContractPlacementModal } from './ContractPlacementModal';
import { InvestorInstructionsModal } from './InvestorInstructionsModal';
import { useContractsController } from './useContractsController';
import {
	canPlaceContractSignatures,
	findPersistedContractByFileId,
	hasContractChangedFromSaved,
} from './useContractsController.utils';
import type { PlacementTarget } from './Contracts.types';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';

import {
	ContractType,
	DocumentWorkflowTemplateVisibleIn,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Box } from '@mantine/core';

interface Props {
	className?: string;
}

const Contracts = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const contractController = useContractsController([
		DocumentWorkflowTemplateVisibleIn.INVESTMENT_WORKFLOWS_BUYING,
	]);
	const [showCounterpartiesModal, setShowCounterpartiesModal] = useState(false);
	const [showInvestorsModal, setShowInvestorsModal] = useState(false);
	const [placementTarget, setPlacementTarget] = useState<PlacementTarget | null>(null);
	const [isSavingForPlacement, setIsSavingForPlacement] = useState(false);
	const [currentRowIndex, setCurrentRowIndex] = useState(0);
	const [instructionInputValueBackup, setInstructionInputValueBackup] = useState('');

	const openInvestorModal = (id: string) => {
		const currentContractIndex = contractFields.findIndex((item) => item.id === id);
		setCurrentRowIndex(currentContractIndex);
		setShowInvestorsModal(true);
		const contract = getCurrentContract(currentContractIndex);
		setInstructionInputValueBackup(contract.instruction);
	};
	const openCounterpartiesModal = (id: string) => {
		setCurrentRowIndex(contractFields.findIndex((item) => item.id === id));
		setShowCounterpartiesModal(true);
	};
	// Always save the draft before placing. Placement needs a persisted template (a
	// freshly uploaded row only has a client-side UUID), and saving rebuilds the
	// signing roles — which clears existing placements — so the editor must open on
	// the config that was just persisted, never on a stale one.
	const openPlacementModal = async (id: string) => {
		const rowIndex = contractFields.findIndex((item) => item.id === id);
		if (rowIndex < 0) {
			return;
		}
		const row = contractController.contractsMainForm.getValues(`contracts.${rowIndex}`);

		// Saving drops rows with a blank contract name, so refuse rather than lose them.
		const isValid = await contractController.contractsMainForm.trigger();
		if (!isValid) {
			showToastV2({
				title: tMipCreate('contracts.table.errors.placement_requires_contract_name'),
				state: 'error',
			});
			return;
		}

		setIsSavingForPlacement(true);
		const savedContracts = await contractController.saveContractsDraft();
		setIsSavingForPlacement(false);

		const persisted = findPersistedContractByFileId(savedContracts, row.file.id);
		if (!persisted) {
			return;
		}
		setPlacementTarget({
			templateId: persisted.id,
			fileId: persisted.file.id,
			contractName: persisted.name,
		});
	};
	const { onRemoveRowClicked, contractFields, showPreviewDocument, remoteContracts } =
		contractController;

	const onInstructionLocalSave = () => {
		contractController.updateFields(currentRowIndex, getCurrentContract());
		setShowInvestorsModal(false);
		setInstructionInputValueBackup('');
	};

	const onCounterpartiesLocalSave = () => {
		contractController.updateFields(currentRowIndex, getCurrentContract());
		setShowCounterpartiesModal(false);
	};
	const onInstructionLocalCancel = () => {
		const contract = getCurrentContract();

		contractController.contractsMainForm.setValue(`contracts.${currentRowIndex}`, {
			...contract,
			instruction: instructionInputValueBackup,
		});
		setInstructionInputValueBackup('');
		setShowInvestorsModal(false);
	};
	const getCurrentContract = (currentContractIndex?: number) => {
		return contractController.contractsMainForm.getValues(
			`contracts.${currentContractIndex ?? currentRowIndex}`
		);
	};
	return (
		<div className={className}>
			<ModalLayer>
				{renderMIPCreateSectionHeaderPortal(
					<SectionHeader
						title={tMipCreate('contracts.header.title')}
						subtitle={tMipCreate('contracts.header.subtitle')}
					/>
				)}

				<FormProvider {...contractController.contractsMainForm}>
					{showInvestorsModal && (
						<InvestorInstructionsModal
							onClose={() => setShowInvestorsModal(false)}
							rowIndex={currentRowIndex}
							onSave={onInstructionLocalSave}
							onCancel={onInstructionLocalCancel}
						/>
					)}
					{showCounterpartiesModal ? (
						<ContractCounterpartiesModal
							show={showCounterpartiesModal}
							onClose={() => setShowCounterpartiesModal(false)}
							rowIndex={currentRowIndex}
							onSave={onCounterpartiesLocalSave}
						/>
					) : null}
					{placementTarget ? (
						<ContractPlacementModal
							show
							onClose={() => setPlacementTarget(null)}
							templateId={placementTarget.templateId}
							fileId={placementTarget.fileId}
							contractName={placementTarget.contractName}
						/>
					) : null}
					<Box data-testid={TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_EXPANDABLE}>
						<ContractsExpandable />
					</Box>

					<SectionDocuments>
						<DocumentsHeader
							size="h5b"
							title={tMipCreate('contracts.documents.title')}
						/>

						<form>
							<DefaultTable maxSize>
								<HeaderRow>
									<HeaderCell size={0.5}>#</HeaderCell>
									<HeaderCell customSize="1fr">
										{tMipCreate('contracts.table.headers.filename')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.contract_name'
										)}
									>
										{tMipCreate('contracts.table.headers.contract_name')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.investor_signature'
										)}
									>
										{tMipCreate('contracts.table.headers.investor_signature')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.contract_type'
										)}
									>
										{tMipCreate('contracts.table.headers.contract_type')}
									</HeaderCell>
									<HeaderCell
										childrenAlign="center"
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.witness'
										)}
									>
										{tMipCreate('contracts.table.headers.witness')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.counterparty_signature'
										)}
									>
										{tMipCreate('contracts.table.headers.counterparties')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.placement'
										)}
									>
										{tMipCreate('contracts.table.headers.placement')}
									</HeaderCell>
									<HeaderCell
										customSize="1fr"
										childrenTooltip={tMipCreate(
											'contracts.table.tooltips.instruction_to_investors'
										)}
									>
										{tMipCreate('contracts.table.headers.instructions')}
									</HeaderCell>
									<HeaderCell size={2} />
								</HeaderRow>
								<BodyRow>
									{contractFields?.length
										? contractFields?.map((item, index) => {
												const numberOfCounterparties =
													item.counterparties?.length ?? 0;
												const itemFormName = `contracts.${index}`;
												const {
													signatureType: contractSignatureType,
													type,
												} = contractController.contractsMainForm.getValues(
													`contracts.${index}`
												);

												const isMultisignature =
													type === ContractType.MULTISIGNATURE;
												const isReviewOnly =
													isReviewSignatureType(contractSignatureType);
												const { errors } =
													contractController.contractsMainForm.formState;
												const isInvalid = !!get(
													errors,
													`contracts.${index}.name`
												);

												const isForm = type === ContractType.FORM;

												// Wet ink and review-only have no electronic
												// slots, a FORM places its own, and a
												// multi-signature contract is shared by every
												// signer — none of them can be placed, so the
												// column stays empty for that row.
												const canPlaceSignatures =
													canPlaceContractSignatures({
														type,
														signatureType: contractSignatureType,
													});

												// Only a saved contract can have placements to invalidate; a
												// local row has none yet.
												const savedContract = remoteContracts?.find(
													(remote) => remote.id === item.id
												);
												const formContract =
													contractController.contractsMainForm.getValues(
														`contracts.${index}`
													);
												const placementNeedsRedo =
													!!savedContract &&
													hasContractChangedFromSaved(
														{
															...formContract,
															requiredWitnesses:
																formContract.hasWitnesses ? 1 : 0,
														},
														savedContract
													);

												return (
													<RowWrapper
														key={`row_${index}`}
														isInvalid={isInvalid}
													>
														<Row>
															<Cell>
																<Text bold variant="p-sm">
																	{`${index + 1}.`}
																</Text>
															</Cell>
															<Cell>
																<Box
																	data-testid={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_SHOW_CONTRACT
																	}
																	data-testindex={index}
																>
																	<ShowPreviewWrapper
																		onClick={() => {
																			void showPreviewDocument(
																				item.id
																			);
																		}}
																	>
																		<Text
																			bold
																			variant="p-sm"
																			elipsis
																		>
																			{item?.file.name ===
																			'n/a.pdf'
																				? 'n/a'
																				: item?.file.name}
																		</Text>
																	</ShowPreviewWrapper>
																</Box>
															</Cell>
															<Cell>
																<Box
																	data-testid={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_CONTRACT_NAME_INPUT
																	}
																	data-testindex={index}
																>
																	<ContractNameInput
																		name={`${itemFormName}.name`}
																	/>
																</Box>
															</Cell>
															<Cell>
																<Box
																	data-testid={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_INVESTOR_SIGNATURE_SELECT
																	}
																	data-testindex={index}
																>
																	<Select
																		name={`${itemFormName}.signatureType`}
																	>
																		<Option
																			value={
																				SignatureType.ELECTRONIC
																			}
																		>
																			{tMipCreate(
																				'contracts.table.signature_types.electronic'
																			)}
																		</Option>
																		<Option
																			value={
																				SignatureType.WET_INK
																			}
																		>
																			{tMipCreate(
																				'contracts.table.signature_types.wet_ink'
																			)}
																		</Option>
																		<Option
																			value={
																				SignatureType.REVIEW
																			}
																			disabled={
																				isMultisignature ||
																				isForm
																			}
																		>
																			{tMipCreate(
																				'contracts.table.signature_types.review'
																			)}
																		</Option>
																	</Select>
																</Box>
															</Cell>
															<Cell>
																<Box
																	data-testid={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_CONTRACT_TYPE_SELECT
																	}
																	data-testindex={index}
																>
																	<Select
																		name={`${itemFormName}.type`}
																	>
																		<Option
																			value={
																				ContractType.DEAL
																			}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.deal'
																			)}
																		</Option>
																		<Option
																			value={
																				ContractType.MULTISIGNATURE
																			}
																			disabled={isReviewOnly}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.multisignature'
																			)}
																		</Option>
																		<Option
																			value={
																				ContractType.SIDE_LETTER
																			}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.side_letter'
																			)}
																		</Option>
																		<Option
																			value={
																				ContractType.FORM
																			}
																			disabled={
																				!!numberOfCounterparties
																			}
																		>
																			{tMipCreate(
																				'contracts.table.contract_types.form'
																			)}
																		</Option>
																	</Select>
																</Box>
															</Cell>
															<Cell align="center">
																<FormCheckbox
																	name={`${itemFormName}.hasWitnesses`}
																	disabled={
																		isReviewOnly || isForm
																	}
																/>
															</Cell>
															<Cell>
																{numberOfCounterparties &&
																!isReviewOnly ? (
																	<EditWrapper>
																		<ChipContract
																			prefixIcon="checkmark-circle-contained-filled"
																			size="s"
																			color={COLOR.GREEN}
																			chipTheme={
																				CHIP_THEME.LIGHT
																			}
																		>
																			{/* display numbers near checkmark if there is more than one counterparties*/}
																			{numberOfCounterparties >
																			1
																				? numberOfCounterparties.toString()
																				: ''}
																		</ChipContract>
																		<Box
																			data-testid={
																				TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_EDIT_COUNTERPARTY_BUTTON
																			}
																			data-testindex={index}
																		>
																			<EditButton
																				onClick={() =>
																					openCounterpartiesModal(
																						item.id
																					)
																				}
																			/>
																		</Box>
																	</EditWrapper>
																) : (
																	<Box
																		data-testid={
																			TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_ADD_COUNTERPARTY_BUTTON
																		}
																		data-testindex={index}
																	>
																		<AddButton
																			disabled={
																				isReviewOnly ||
																				isForm
																			}
																			onClick={() =>
																				openCounterpartiesModal(
																					item.id
																				)
																			}
																		/>
																	</Box>
																)}
															</Cell>
															<Cell>
																{canPlaceSignatures ? (
																	<EditWrapper>
																		{placementNeedsRedo ? (
																			<Tooltip
																				hoverChildrenInsteadIcon
																				tooltip={tMipCreate(
																					'contracts.table.tooltips.placement_needs_redo'
																				)}
																			>
																				<ChipContract
																					prefixIcon="warning"
																					iconSize="lg"
																					size="s"
																					color={
																						COLOR.ORANGE
																					}
																					chipTheme={
																						CHIP_THEME.LIGHT
																					}
																				/>
																			</Tooltip>
																		) : null}
																		<EditButton
																			onClick={() => {
																				void openPlacementModal(
																					item.id
																				);
																			}}
																			disabled={
																				isSavingForPlacement
																			}
																		/>
																	</EditWrapper>
																) : null}
															</Cell>
															<Cell>
																{item.instruction?.length ? (
																	<EditWrapper>
																		<ChipContract
																			prefixIcon="checkmark-circle-contained-filled"
																			size="s"
																			color={COLOR.GREEN}
																			chipTheme={
																				CHIP_THEME.LIGHT
																			}
																		/>
																		<Box
																			data-testid={
																				TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_EDIT_INSTRUCTION_BUTTON
																			}
																			data-testindex={index}
																		>
																			<EditButton
																				onClick={() =>
																					openInvestorModal(
																						item.id
																					)
																				}
																			/>
																		</Box>
																	</EditWrapper>
																) : (
																	<Box
																		data-testid={
																			TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_ADD_INSTRUCTION_BUTTON
																		}
																		data-testindex={index}
																	>
																		<AddButton
																			onClick={() =>
																				openInvestorModal(
																					item.id
																				)
																			}
																		/>
																	</Box>
																)}
															</Cell>
															<Cell>
																<Box
																	data-testid={
																		TEST_ELEMENT_IDS.MIP_CREATE_CONTRACTS_DELETE_CONTRACT_BUTTON
																	}
																	data-testindex={index}
																>
																	<DeleteButtonSquare
																		onClick={() => {
																			onRemoveRowClicked(
																				item.id
																			);
																		}}
																	/>
																</Box>
															</Cell>
														</Row>
													</RowWrapper>
												);
											})
										: null}
								</BodyRow>
							</DefaultTable>
						</form>

						<DragAndDropWrapper
							uploadFileRequest={contractController.uploadFileRequest}
							accept={{ 'application/pdf': ['.pdf'] }}
							invalidTypeMessage={tMipCreate(
								'contracts.table.errors.invalid_file_type'
							)}
						/>
					</SectionDocuments>
				</FormProvider>
			</ModalLayer>
		</div>
	);
};
const ShowPreviewWrapper = styled.div`
	width: 100%;
	cursor: pointer;
`;
const ChipContract = styled(ChipsV2)`
	height: 1.25rem;
	padding: 2px 12px;
	width: fit-content;
`;
const AddButton = styled(
	({
		className,
		onClick,
		disabled,
	}: {
		className?: string;
		onClick: () => void;
		disabled?: boolean;
	}) => {
		const tMipCreate = useTranslationMipCreate();
		return (
			<Button
				onClick={onClick}
				className={`${className} clickAnimation`}
				iconSize="lg"
				icon="add-circle"
				iconPlacement="left"
				size="medium"
				variant="text"
				disabled={disabled}
			>
				{tMipCreate('contracts.table.add')}
			</Button>
		);
	}
)``;
const EditWrapper = styled.div`
	display: flex;
	flex-direction: row;
	padding: 0px 5px;
	justify-content: center;
	align-items: center;
`;

const EditButton = styled(
	({
		className,
		onClick,
		disabled,
	}: {
		className?: string;
		onClick: () => void;
		disabled?: boolean;
	}) => {
		const tMipCreate = useTranslationMipCreate();

		return (
			<Button
				onClick={onClick}
				className={`${className} clickAnimation`}
				iconSize="lg"
				icon="edit-pencil"
				iconPlacement="left"
				size="medium"
				variant="text"
				disabled={disabled}
			>
				{tMipCreate('contracts.table.edit')}
			</Button>
		);
	}
)`
	padding: 12px;
`;

const SectionDocuments = styled.div``;
const DragAndDropWrapper = styled(DragAndDrop)``;
const DocumentsHeader = styled(SectionHeader)`
	margin-top: 2em;
`;

const ContractNameInput = styled(StringInput)``;

const Styled = styled(Contracts)`
	${DeleteButtonSquare} {
		margin-left: 2em;
	}

	${DragAndDropWrapper} {
		margin-top: 4.75em;
	}

	${DefaultTable} {
		overflow: unset;
	}

	${HeaderRow} {
		align-items: center;
	}

	${FormCheckbox} {
		input:not(:checked) {
			border: 1px solid ${defaultTheme.v3.colors.gray[4]};
		}
	}
`;

export { Styled as Contracts };
